import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import SuccessAlert from './../child/Alert/SuccessAlert';
import DengerAlert from './../child/Alert/DengerAlert';

const SignInLayer = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const domains = ["gmail.com", "yahoo.com", "outlook.com"];
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const [passwordRules, setPasswordRules] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false,
    noPersonalInfo: false,
  });

  const navigate = useNavigate();

  const handleEmailChange = (e) => {
  const value = e.target.value;
  setEmail(value);
  localStorage.setItem("signinemail", value);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!value) {
    setErrors({ email: "Email is required" });
  } else if (!emailRegex.test(value)) {
    setErrors({ email: "Please enter a valid email address" });
  } else {
    setErrors({});
  }

  const atIndex = value.indexOf("@");
  if (atIndex !== -1) {
    const enteredDomain = value.slice(atIndex + 1).toLowerCase();
    const matched = domains.filter((domain) =>
      domain.toLowerCase().startsWith(enteredDomain)
    );
    setSuggestions(matched);
    setShowSuggestions(true);
  } else {
    setShowSuggestions(false);
  }
};

const handleSuggestionClick = (domain) => {
  const atIndex = email.indexOf("@");
  const localPart = email.slice(0, atIndex);
  const newEmail = `${localPart}@${domain}`;
  setEmail(newEmail);
  setSuggestions([]);
  setShowSuggestions(false);

  // ✅ Re-run validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!newEmail) {
    setErrors({ email: "Email is required" });
  } else if (!emailRegex.test(newEmail)) {
    setErrors({ email: "Please enter a valid email address" });
  } else {
    setErrors({});
  }
};

  useEffect(() => {
    const savedEmail = localStorage.getItem("rememberedEmail");
    const savedPassword = localStorage.getItem("rememberedPassword");
    if (savedEmail && savedPassword) {
      setEmail(savedEmail);
      setPassword(savedPassword);
      setRememberMe(true);
      localStorage.setItem("signinemail", savedEmail); // Fix: set actual savedEmail, not empty
    }
    const token = localStorage.getItem("authToken");
    if (token) {
      navigate("/dashboard");
    }
    document.body.classList.add("signin-page");
    return () => document.body.classList.remove("signin-page");
  }, [navigate]);

  useEffect(() => {
    const hasLength = password.length >= 8 && password.length <= 16;
    const hasUpper = /[A-Z]/.test(password);
    const hasLower = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecial = /[!@#$%^&*]/.test(password);
    const hasNoPersonalInfo =
      password &&
      !password.toLowerCase().includes(email?.toLowerCase()) &&
      !password.toLowerCase().includes(email?.split("@")[0].toLowerCase());

    setPasswordRules({
      length: hasLength,
      uppercase: hasUpper,
      lowercase: hasLower,
      number: hasNumber,
      special: hasSpecial,
      noPersonalInfo: hasNoPersonalInfo,
    });

    setShowRules(password.length > 0);
  }, [password, email]);

  const getRuleClass = (isValid) => (isValid ? "text-success" : "text-danger");

  const isFormValid =
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) &&
    passwordRules.length &&
    passwordRules.uppercase &&
    passwordRules.lowercase &&
    passwordRules.number &&
    passwordRules.special &&
    passwordRules.noPersonalInfo;

  const validateInputs = () => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!email) {
      newErrors.email = "Email is required";
    } else if (!emailRegex.test(email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!password) {
      newErrors.password = "Password is required";
    } else if (password.length < 8) {
      newErrors.password = "Password must be at least 8 characters";
    } else if (password.length > 16) {
      newErrors.password = "Password must not exceed 16 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!validateInputs()) return;

    setLoading(true);
    const API_ENDPOINT = `${process.env.REACT_APP_BASE_URL}/admin/signIn`;

    try {
      const response = await fetch(API_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.message === "User not found.") {
          setErrors((prev) => ({
            ...prev,
            email: "The email address appears to be incorrect. Please verify it.",
          }));
        } else if (data.message === "Password Incorrect") {
          setErrors((prev) => ({
            ...prev,
            password: "The password is incorrect. Please verify it.",
          }));
        } else {
          setErrorMessage(data.message || "Login failed. Please try again.");
        }
        setTimeout(() => setErrorMessage(""), 3000);
      } else {
        const user = data.user || {};
        localStorage.setItem("authToken", data.token);
        localStorage.setItem("name", user.name || "");
        localStorage.setItem("admin_id", user._id || "");
        localStorage.setItem("profile_picture", user.profilePicture || "");
        localStorage.setItem("user_role", user.role || "");
        localStorage.setItem("permissions", JSON.stringify(user.permissions || []));

        if (rememberMe) {
          localStorage.setItem("rememberedEmail", email);
          localStorage.setItem("rememberedPassword", password);
        } else {
          localStorage.removeItem("rememberedEmail");
          localStorage.removeItem("rememberedPassword");
        }

        localStorage.setItem("signinemail", email); // ✅ Save email to be reused

        setSuccessMessage("Sign in successful");
        setTimeout(() => {
          setSuccessMessage("");
          navigate("/dashboard");
        }, 3000);
      }
    } catch (error) {
      setErrorMessage("An unexpected error occurred. Please try again.");
      console.error(error);
      setTimeout(() => setErrorMessage(""), 3000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="auth bg-base d-flex flex-wrap">
      {successMessage && <SuccessAlert message={successMessage} onClose={() => setSuccessMessage("")} />}
      {errorMessage && <DengerAlert message={errorMessage} onClose={() => setErrorMessage("")} />}

      <div className="auth-left d-lg-block d-none">
        <div className="d-flex align-items-center flex-column h-100 justify-content-center">
          <img src={`${process.env.REACT_APP_PUBLIC_UR}/assets/images/auth/auth-img.png`} alt="Auth" />
        </div>
      </div>

      <div className="auth-right py-32 px-24 d-flex flex-column justify-content-center">
        <div className="max-w-464-px mx-auto w-100">
          <div>
            {/* <img src={`${process.env.REACT_APP_PUBLIC_UR}/assets/images/logo.png`}  alt="Logo"  /> */}
            <h4 className="mb-12">Sign In to your Account</h4>
            <p className="mb-32 text-secondary-light text-lg">
              Welcome back! Please enter your details.
            </p>
          </div>

          <form onSubmit={handleLogin}>
            <div className="icon-field mb-16">
              <span className="icon top-50 translate-middle-y">
                <Icon icon="mage:email" />
              </span>
               <input
                type="email"
                className="form-control h-56-px bg-neutral-50 radius-12"
                placeholder="Enter Email"
                autoComplete="email"
                maxLength="80"
                value={email}
                onChange={handleEmailChange}
               /* onChange={(e) => {
                  const value = e.target.value;
                  setEmail(value);
                  localStorage.setItem("signinemail", e.target.value);
                  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                  if (!value) {
                    setErrors({ email: "Email is required" });
                  } else if (!emailRegex.test(value)) {
                    setErrors({ email: "Please enter a valid email address" });
                  } else {
                    setErrors({});
                  }
                }}*/
              />
             {showSuggestions && suggestions.length > 0 && (
              <ul className="suggestion-box bg-white border p-2 radius-8 mt-1 shadow-sm" style={{ listStyle: 'none', margin: 0 }}>
                {suggestions.map((domain, index) => (
                  <li
                    key={index}
                    className="px-2 py-1 hover:bg-neutral-100 cursor-pointer"
                    onClick={() => handleSuggestionClick(domain)}
                  >
                    {`${email.split("@")[0]}@${domain}`}
                  </li>
                ))}
              </ul>
            )}
            </div>

            {errors.email && <p className="text-danger mt-1">{errors.email}</p>}
            <div className="position-relative mb-20">
              <div className="icon-field">
                <span className="icon top-50 translate-middle-y">
                  <Icon icon="solar:lock-password-outline" />
                </span>
                <input
                  type={showPassword ? "text" : "password"}
                  className="form-control h-56-px bg-neutral-50 radius-12"
                  id="your-password"
                  placeholder="Enter Password"
                  autoComplete="current-password"
                  maxLength={16}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setErrors((prev) => ({ ...prev, password: "" }));
                  }}
                  onFocus={() => setShowRules(true)}
                />
                <span
                  className="toggle-password cursor-pointer position-absolute end-0 top-50 translate-middle-y me-16 text-secondary-light"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  <Icon
                    icon={showPassword ? "solar:eye-bold" : "solar:eye-closed-bold"}
                    style={{ verticalAlign: 'middle' }}
                  />
                </span>
              </div>

              {showRules && (
                <ul style={{ fontSize: "12px", paddingLeft: "20px" }}>
                  <li className={getRuleClass(passwordRules.length)}>
                    Password must be between 8 and 16 characters
                  </li>
                  <li className={getRuleClass(passwordRules.uppercase)}>
                    Must include at least one uppercase letter (A–Z)
                  </li>
                  <li className={getRuleClass(passwordRules.lowercase)}>
                    Must include at least one lowercase letter (a–z)
                  </li>
                  <li className={getRuleClass(passwordRules.number)}>
                    Must include a number (0–9)
                  </li>
                  <li className={getRuleClass(passwordRules.special)}>
                    Must include at least one special character (!@#$%^&*)
                  </li>
                  <li className={getRuleClass(passwordRules.noPersonalInfo)}>
                    Should not contain your name or email
                  </li>
                </ul>
              )}
            </div>
            {errors.password && <p className="text-danger mt-1">{errors.password}</p>}

            <div className="d-flex justify-content-between gap-2">
              <div className="form-check style-check d-flex align-items-center">
                <input
                  className="form-check-input border border-neutral-300"
                  type="checkbox"
                  id="remember"
                  checked={rememberMe}
                  onChange={() => setRememberMe(!rememberMe)}
                />
                <label className="form-check-label" htmlFor="remember">
                  Remember me
                </label>
              </div>
              <Link to="/forgotpassword" className="text-primary-600 fw-medium">
                Forgot Password?
              </Link>
            </div>

            <button
              type="submit"
              className="btn btn-primary btn-sm px-12 py-16 w-100 radius-12 mt-32"
              disabled={loading || !isFormValid}
              style={{ fontSize: "18px" }}
            >
              {loading ? "Signing In..." : "Sign In"}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default SignInLayer;
